// =============================================================================
// Create Workspace Modal - 紧凑的工作空间创建对话框
// =============================================================================

import React, { useState, useEffect } from 'react';
import { X, Plus, Code, Settings, Cpu, HardDrive } from 'lucide-react';
import { workspaceService } from '../services/workspaceService';
import LoadingSpinner from './LoadingSpinner';

const CreateWorkspaceModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('nodejs');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: 'nodejs',
    resources: {
      memory: '1g',
      cpu: '1'
    }
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      const templatesData = await workspaceService.getTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setError('Failed to load workspace templates');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setFormData(prev => ({
      ...prev,
      templateId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Workspace name is required');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const workspace = await workspaceService.createWorkspace(formData);
      onSuccess(workspace);
      handleClose();
    } catch (error) {
      console.error('Failed to create workspace:', error);
      setError(error.message || 'Failed to create workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      templateId: 'nodejs',
      resources: { memory: '1g', cpu: '1' }
    });
    setSelectedTemplate('nodejs');
    setError('');
    onClose();
  };

  const getTemplateIcon = (templateId) => {
    switch (templateId) {
      case 'nodejs': return '🟢';
      case 'python': return '🐍';
      case 'go': return '🔵';
      case 'rust': return '🦀';
      default: return '📦';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plus className="w-5 h-5" />
            创建工作空间
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-accent rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                工作空间名称 *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="我的项目"
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                描述 (可选)
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="项目描述"
                className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-1">
              <Code className="w-4 h-4" />
              选择模板
            </label>
            <div className="grid grid-cols-2 gap-2">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`p-3 border rounded cursor-pointer transition-all text-sm ${
                    selectedTemplate === template.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getTemplateIcon(template.id)}</span>
                    <span className="font-medium">{template.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {template.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {template.languages?.slice(0, 2).map((lang) => (
                      <span
                        key={lang}
                        className="px-1.5 py-0.5 text-xs bg-secondary text-secondary-foreground rounded"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Resource Configuration */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-1">
              <Settings className="w-4 h-4" />
              资源配置
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <HardDrive className="w-3 h-3" />
                  内存限制
                </label>
                <select
                  value={formData.resources.memory}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    resources: { ...prev.resources, memory: e.target.value }
                  }))}
                  className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="512m">512 MB</option>
                  <option value="1g">1 GB</option>
                  <option value="2g">2 GB</option>
                  <option value="4g">4 GB</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <Cpu className="w-3 h-3" />
                  CPU 限制
                </label>
                <select
                  value={formData.resources.cpu}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    resources: { ...prev.resources, cpu: e.target.value }
                  }))}
                  className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="0.5">0.5 CPU</option>
                  <option value="1">1 CPU</option>
                  <option value="2">2 CPU</option>
                  <option value="4">4 CPU</option>
                </select>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-sm border border-border rounded hover:bg-accent"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  创建中...
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  创建工作空间
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkspaceModal;
